import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { FaUpload, FaTrash, FaFileAlt } from "react-icons/fa";
import {
  listInspections,
  createInspection,
  updateInspection,
  markInspectionDone,
  resetInspection,
  deleteInspection,
  listUsers,
  listClaimFiles,
  fileContentUrl,
  uploadInspectionFiles,
  deleteInspectionFile,
  renameClaimFile,
} from "../../services/apiClient.js";
import DropZone from "../shared/DropZone.jsx";
import { rowLabel, selectableRows } from "../../utils/people.js";
import FileCard, { Lightbox } from "./FileCard.jsx";
import ZipAllPhotos from "../../experiments/photo-helix/ZipAllPhotos.jsx"; // experiment, see its README
import "../../styles/dropzone.css";
import { getSessionUser } from "../../services/pageDefaults.js";
import { formatDate, formatDateTime } from "../../utils/date.js";
import TabHeader from "./TabHeader.jsx";

// Inspections as a true 1-to-many collection. "Mark Inspected" advances the claim
// to Inspected through the status engine (a reinspection if already past I).
const EMPTY = {
  scheduled_date: "", inspector_id: "", notes_for_inspector: "",
  date_inspected: "", notes: "", scheduled_confirmed: false,
};

function ClaimInspectionTab({ claimId, onChanged }) {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [photos, setPhotos] = useState([]);
  // Manually uploaded inspection paperwork — quotes, sketches, notes. Kept
  // separate from photos because a PDF in a thumbnail grid is a broken tile.
  const [docs, setDocs] = useState([]);
  const [uploadingFor, setUploadingFor] = useState(null); // inspection id
  const [lightboxId, setLightboxId] = useState(null);
  const fileInput = useRef(null);
  const uploadTarget = useRef(null);
  const [mode, setMode] = useState("list"); // list | add | edit
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");

  const sessionUser = getSessionUser();
  const isAdmin = !!sessionUser?.is_admin;
  // Reset belongs to the inspector workspace, so it stays out of sight entirely
  // until the owner has switched that workspace on.
  const inspectionWorkspace = !!sessionUser?.inspection_workspace_enabled;
  // Nothing recorded and no photos means there is nothing a reset would undo.
  const canReset = (i) =>
    !!i.has_been_inspected || !!i.date_inspected || Number(i.photo_count || 0) > 0;

  function loadPhotos() {
    return Promise.all([
      listClaimFiles(claimId, "inspection_photo").then(setPhotos).catch(() => setPhotos([])),
      listClaimFiles(claimId, "inspection_doc").then(setDocs).catch(() => setDocs([])),
    ]);
  }

  // --- Manual uploads (1.0's InspectionPage uploader) ------------------------
  // One hidden input for the whole tab; the button that opened it says which
  // inspection the files belong to.
  function pickFiles(inspectionId) {
    setError("");
    uploadTarget.current = inspectionId;
    fileInput.current?.click();
  }
  async function sendFiles(inspectionId, files) {
    if (!files?.length) return;
    setError("");
    setUploadingFor(inspectionId);
    try {
      await uploadInspectionFiles(claimId, inspectionId, files);
      await loadPhotos();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Upload failed.");
    } finally {
      setUploadingFor(null);
    }
  }
  async function removeFile(inspectionId, file) {
    if (!window.confirm(`Remove ${file.original_filename || "this file"} from the inspection?`)) return;
    try {
      await deleteInspectionFile(claimId, inspectionId, file.id);
      await loadPhotos();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to remove the file.");
    }
  }
  // Inspection files are claim_files rows, so the claim-level rename applies.
  // Errors stay in the card (FileCard shows them beside the input), so no catch;
  // no onChanged — a name changes nothing any badge or count reads.
  async function renameFile(file, name) {
    await renameClaimFile(claimId, file.id, name);
    await loadPhotos();
  }
  function load() {
    listInspections(claimId).then(setItems).catch(() => setError("Unable to load inspections."));
  }
  useEffect(() => {
    load();
    listUsers({ inspectors: true }).then(setUsers).catch(() => {});
    loadPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  // Group by inspection, and keep a bucket for photos whose inspection has since
  // been deleted (claim_files.inspection_id is ON DELETE SET NULL) — they still
  // belong to the claim and must not vanish behind an empty heading.
  const photoGroups = useMemo(() => {
    const known = new Set(items.map((i) => Number(i.id)));
    // EVERY inspection gets a group now, empty or not: the group header carries
    // the upload control, so hiding empty ones would hide the only way to put
    // the first file on a job that was inspected the old way (Mia, 2026-08-06).
    const groups = items.map((inspection) => ({
      key: `inspection-${inspection.id}`,
      id: inspection.id,
      label: `Inspection #${inspection.seq}${inspection.scheduled_date ? ` · ${formatDate(inspection.scheduled_date)}` : ""}`,
      files: [
        ...photos.filter((p) => Number(p.inspection_id) === Number(inspection.id)),
        ...docs.filter((d) => Number(d.inspection_id) === Number(inspection.id)),
      ],
    }));
    const orphanPhotos = photos.filter(
      (photo) => photo.inspection_id == null || !known.has(Number(photo.inspection_id))
    );
    const orphanDocs = docs.filter(
      (doc) => doc.inspection_id == null || !known.has(Number(doc.inspection_id))
    );
    if (orphanPhotos.length || orphanDocs.length) {
      groups.push({
        key: "unlinked",
        id: null, // nothing to upload against, so that group shows no uploader
        label: "Not linked to a current inspection",
        files: [...orphanPhotos, ...orphanDocs],
      });
    }
    return groups;
  }, [items, photos, docs]);

  // Full-screen paging runs across EVERY inspection image on the claim, not just
  // the group clicked — the office flicks through a whole visit, and stopping at
  // a group boundary is a surprise.
  const allInspectionFiles = useMemo(
    () => photoGroups.flatMap((g) => g.files),
    [photoGroups]
  );

  const galleryImages = useMemo(
    () => photoGroups.flatMap((g) => g.files.filter((f) => f.is_image)),
    [photoGroups]
  );

  function handle(event) {
    const { name, value, type, checked } = event.target;
    setForm((c) => ({ ...c, [name]: type === "checkbox" ? checked : value }));
  }
  function startAdd() {
    setForm(EMPTY);
    setEditingId(null);
    setMode("add");
    setError("");
  }
  function startEdit(i) {
    setForm({
      scheduled_date: i.scheduled_date || "",
      inspector_id: i.inspector_id || "",
      notes_for_inspector: i.notes_for_inspector || "",
      date_inspected: i.date_inspected || "",
      notes: i.notes || "",
      scheduled_confirmed: !!i.scheduled_confirmed,
    });
    setEditingId(i.id);
    setMode("edit");
    setError("");
  }
  async function save() {
    try {
      if (mode === "add") await createInspection(claimId, form);
      else await updateInspection(claimId, editingId, form);
      setMode("list");
      load();
      // ADDING an inspection re-requests the claim server-side (services/
      // inspections.js), so this changes the claim, not just this tab. Without
      // it the rail kept showing "Accepted · 38 days" on a job that had just
      // been re-requested — the screenshot Robert queried on 13 Aug, and very
      // likely what convinced the office the status was staying Accepted on
      // purpose. Every other handler in this file already fires it.
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save inspection.");
    }
  }
  async function markDone(row) {
    // Built from a FRESH read, for the same reason reset is (see below): the
    // office keeps a claim open all day, and this dialog's photo count is their
    // only check that the inspector's queue actually drained. A count from
    // page-load time can be missing everything that arrived since.
    let i = row;
    let stale = false;
    try {
      const current = await listInspections(claimId);
      setItems(current);
      const fresh = current.find((x) => Number(x.id) === Number(row.id));
      if (!fresh) {
        setError(`Inspection #${row.seq} no longer exists. The list has been refreshed.`);
        return;
      }
      if (fresh.office_accepted) {
        setError(`Inspection #${fresh.seq} has already been accepted. The list has been refreshed.`);
        return;
      }
      i = fresh;
    } catch {
      stale = true; // could not check — say so rather than quote a stale number
    }
    // What the inspector's phone said was still coming, against what has landed.
    const outstanding = Number(i.photos_outstanding || 0);
    const photoLine = stale
      ? "\n\nWARNING: the photo count could not be refreshed just now, so it may be out of date."
      : outstanding > 0
        ? `\n\nWARNING: ${outstanding} photo${outstanding === 1 ? "" : "s"} ` +
          `${outstanding === 1 ? "has" : "have"} not arrived yet — the inspector's phone was ` +
          "still sending when they finished. Accepting now accepts an incomplete set."
        : "";
    // If the inspector wrote something on site, it belongs in front of the person
    // deciding whether to accept — not one scroll away on the tab.
    const notesLine = i.notes_from_inspector
      ? `\n\nNotes from ${i.inspector_name || "the inspector"}:\n${i.notes_from_inspector}`
      : "";
    // Two ways in: the office recording it themselves, or accepting what the
    // inspector finished on site. Both land on the same status move.
    const message = (i.awaiting_acceptance
      ? `Accept inspection #${i.seq}?\n\n` +
        `${i.inspector_name || "The inspector"} finished it` +
        `${i.date_inspected ? ` on ${formatDate(i.date_inspected)}` : ""}` +
        ` with ${i.photo_count || 0} photo${i.photo_count === 1 ? "" : "s"}. ` +
        "This moves the claim to Inspected."
      : `Record inspection #${i.seq} as done? This moves the claim to Inspected.`) + notesLine + photoLine;
    if (!window.confirm(message)) return;
    setError("");
    try {
      const result = await markInspectionDone(claimId, i.id);
      load();
      if (onChanged) onChanged();
      // The engine refuses a move the claim isn't in shape for and says so
      // quietly (409). Without this the button would look like it did nothing.
      if (result && result.status_advanced === false) {
        setError(
          "Inspection recorded, but the claim's status did not move to Inspected — " +
          "it is not a valid step from where the claim is now. Check the Status tab."
        );
      }
    } catch (err) {
      setError(err.response?.data?.error || "Unable to record inspection.");
    }
  }
  async function reset(i) {
    // Count from a FRESH read, not from `photos`. That list is loaded when the
    // tab opens and never refreshes, so during a live test it goes stale within
    // minutes: on 2026-07-29 this promised "no photos to delete" and then
    // deleted nine the inspector had just sent. A confirm on a destructive
    // action has to be true at the moment it is shown.
    let shots = null;
    try {
      const current = await listClaimFiles(claimId, "inspection_photo");
      setPhotos(current);
      shots = current.filter((p) => Number(p.inspection_id) === Number(i.id)).length;
    } catch {
      shots = null; // could not check — say so rather than claim zero
    }
    const photoLine =
      shots === null
        ? "\n\nWARNING: could not check for inspector photos just now. Any on this inspection WILL still be deleted."
        : shots
          ? `\n\nThe ${shots} inspector photo${shots === 1 ? "" : "s"} on this inspection will be deleted.`
          : "\n\nThere are no inspector photos on this inspection to delete.";
    // Three genuinely different states under split completion, and the dialog
    // should describe the one it is actually in.
    const message = i.office_accepted
      ? `Reset inspection #${i.seq} back to not inspected?\n\n` +
        "This rolls the claim's Inspected status back and clears the inspected " +
        "date, so the job can be walked through again." + photoLine
      : i.has_been_inspected
        ? `Reset inspection #${i.seq} back to not inspected?\n\n` +
          "The inspector finished this but the office has not accepted it yet, " +
          "so the claim's status is untouched. This clears the finish and the " +
          "inspected date." + photoLine
        : `Clear inspection #${i.seq} for a fresh run?\n\n` +
          "It is already not inspected, so the claim's status is untouched." + photoLine;
    if (!window.confirm(message)) return;
    setError("");
    try {
      const result = await resetInspection(claimId, i.id);
      load();
      await loadPhotos();
      if (onChanged) onChanged();
      // Only odd when the office had ACCEPTED it and the rollback still did not
      // happen (an imported status log with no linked event). An inspection the
      // inspector merely finished has no status event by design, so warning
      // there fired on the normal path and read as a fault when nothing was
      // wrong.
      if (i.office_accepted && !result.status_rolled_back) {
        setError(
          "Inspection cleared, but the claim's status was left alone — no Inspected event was linked to it."
        );
      }
    } catch (err) {
      setError(err.response?.data?.error || "Unable to reset inspection.");
    }
  }
  async function remove(i) {
    if (
      !window.confirm(
        `Delete inspection #${i.seq}? This also rolls back its status cycle — its re-request and anything after it.`
      )
    )
      return;
    setError("");
    try {
      await deleteInspection(claimId, i.id);
      load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete inspection.");
    }
  }

  const isForm = mode === "add" || mode === "edit";

  return (
    <>
      {error ? <div className="alert alert-danger">{error}</div> : null}

      <TabHeader
        title="Inspections"
        help="status"
        actions={!isForm ? <Button size="sm" onClick={startAdd}>New Inspection</Button> : null}
      />

      {isForm ? (
        <div className="border rounded p-3 mb-3">
          <h6 className="mb-3">{mode === "add" ? "New Inspection" : "Edit Inspection"}</h6>
          <Row className="g-2">
            <Col xs={12} md={4}>
              <Form.Label className="mb-0 small fw-semibold">Scheduled Date</Form.Label>
              <Form.Control size="sm" type="date" name="scheduled_date" value={form.scheduled_date} onChange={handle} />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="mb-0 small fw-semibold">Inspector</Form.Label>
              <ThemedSelect size="sm" name="inspector_id" value={form.inspector_id} onChange={handle}>
                <option value="">— None —</option>
                {/* Retired accounts are not OFFERED, but the inspector this
                    inspection already names stays listed — the shared rule in
                    utils/people.js. This tab marked them "(inactive)" but never
                    stopped offering them, which was the one assignment surface
                    left out when the rule landed (review, 2026-08-12). */}
                {selectableRows(users, form.inspector_id).map((u) => (
                  <option key={u.id} value={u.id}>{rowLabel(u)}</option>
                ))}
              </ThemedSelect>
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="mb-0 small fw-semibold">Date Inspected</Form.Label>
              <Form.Control size="sm" type="date" name="date_inspected" value={form.date_inspected} onChange={handle} />
            </Col>
            <Col xs={12}>
              <Form.Check className="mt-1" name="scheduled_confirmed" label="Scheduled confirmed" checked={form.scheduled_confirmed} onChange={handle} />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="mb-0 small fw-semibold">Notes for Inspector</Form.Label>
              <Form.Control as="textarea" rows={2} size="sm" name="notes_for_inspector" value={form.notes_for_inspector} onChange={handle} />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="mb-0 small fw-semibold">Notes</Form.Label>
              <Form.Control as="textarea" rows={2} size="sm" name="notes" value={form.notes} onChange={handle} />
            </Col>
          </Row>
          <div className="mt-2">
            <Button size="sm" onClick={save}>Save</Button>
            <Button size="sm" variant="secondary" className="ms-2" onClick={() => setMode("list")}>Cancel</Button>
          </div>
        </div>
      ) : null}

      <Table striped bordered hover responsive size="sm" className="mb-0 align-middle">
        <thead className="table-surface">
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Scheduled</th>
            <th>Inspector</th>
            <th>Inspected</th>
            <th>Done</th>
            <th>Notes</th>
            <th className="text-end" style={{ width: 270 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id}>
              <td>{i.seq}</td>
              <td>{i.scheduled_date ? formatDate(i.scheduled_date) : "-"}{i.scheduled_confirmed ? " ✓" : ""}</td>
              <td>{i.inspector_name || "-"}</td>
              <td>{i.date_inspected ? formatDate(i.date_inspected) : "-"}</td>
              <td>
                {i.awaiting_acceptance ? (
                  <Badge bg="warning" text="dark">Awaiting acceptance</Badge>
                ) : i.has_been_inspected ? (
                  <Badge bg="success">Yes</Badge>
                ) : "No"}
                {/* The inspector's phone had photos it had not sent yet — don't
                    let anyone accept a partial set without seeing this. */}
                {Number(i.photos_outstanding || 0) > 0 ? (
                  <div className="small text-danger mt-1">
                    {i.photos_outstanding} photo{i.photos_outstanding === 1 ? "" : "s"} still coming
                  </div>
                ) : null}
              </td>
              <td style={{ whiteSpace: "pre-wrap" }}>
                {i.notes || (i.notes_from_inspector ? "" : "-")}
                {/* The inspector's own field, written on site. Separate from the
                    office's notes above it so neither can save over the other. */}
                {i.notes_from_inspector ? (
                  <div className={i.notes ? "border-top mt-2 pt-2" : undefined}>
                    <div className="small fw-semibold text-primary">
                      From inspector
                      {i.notes_from_inspector_at ? ` · ${formatDateTime(i.notes_from_inspector_at)}` : ""}
                    </div>
                    {i.notes_from_inspector}
                  </div>
                ) : null}
              </td>
              <td className="text-end text-nowrap">
                <Button variant="outline-primary" size="sm" className="me-2" onClick={() => startEdit(i)}>Edit</Button>
                {/* The inspector finishing on site does not move the claim —
                    the office accepts, which is the photo checkpoint. */}
                {!i.office_accepted ? (
                  <Button variant="success" size="sm" className="me-2" onClick={() => markDone(i)}>
                    {i.awaiting_acceptance ? "Accept Inspection" : "Mark Inspected"}
                  </Button>
                ) : null}
                {/* Admin-only, and available whether or not it has been marked
                    done: clearing the photos for a fresh run is half the point. */}
                {isAdmin && inspectionWorkspace && canReset(i) ? (
                  <Button
                    variant="outline-warning"
                    size="sm"
                    className="me-2"
                    onClick={() => reset(i)}
                    data-hint="Undo this inspection and delete its inspector photos so the job can be run again"
                  >
                    Reset
                  </Button>
                ) : null}
                <Button variant="outline-danger" size="sm" onClick={() => remove(i)}>Delete</Button>
              </td>
            </tr>
          ))}
          {items.length === 0 ? (
            <tr><td colSpan={7} className="text-center text-muted">No inspections.</td></tr>
          ) : null}
        </tbody>
      </Table>

      {/* INSPECTION FILES. Photos from the inspector app and paperwork uploaded
          by hand live together under the inspection they belong to, which is
          what 1.0's InspectionPage does and what the office asked for: "upload
          all inspection related stuff into the inspection tab" (Mia,
          2026-08-06). Drag onto a group, or use its Upload button. */}
      {photoGroups.length ? (
        <section className="mt-4">
          <div className="d-flex align-items-center gap-2 mb-2">
            <h5 className="mb-0">Inspection Files</h5>
            <Badge bg="secondary">{photos.length + docs.length}</Badge>
            {/* Zip All / Select, as 1.0 carries them at the top of its Inspection
                page. ONE pair for the tab, not one per inspection: tile selection
                is page-global, so several instances would contend for the same
                tiles. Scope is every inspection file on the claim.
                Select → Pick then offers the INSPECTIONS as its sections, so a
                job with six visits can take one of them rather than all six or a
                hand-ticked pile (Karen 2026-08-07). Grouping by file type — the
                Files tab's rule — would be one heading here, since every
                inspection file is typed "Inspector". */}
            {allInspectionFiles.length ? (
              <span className="ms-auto">
                <ZipAllPhotos
                  claimId={claimId}
                  files={allInspectionFiles}
                  groups={photoGroups.map((g) => ({
                    name: g.label,
                    ids: g.files.map((f) => f.id),
                  }))}
                />
              </span>
            ) : null}
          </div>
          <input
            ref={fileInput}
            type="file"
            multiple
            className="d-none"
            onChange={(e) => {
              const picked = Array.from(e.target.files || []);
              const target = uploadTarget.current;
              e.target.value = ""; // let the same file be picked again after a failure
              sendFiles(target, picked);
            }}
          />
          {photoGroups.map((group) => (
            <DropZone
              key={group.key}
              className="mb-3 p-2 border rounded"
              disabled={!group.id || uploadingFor === group.id}
              label={`Drop files onto ${group.label}`}
              onFiles={(files) => sendFiles(group.id, files)}
            >
              <div className="d-flex align-items-center gap-2 mb-2">
                <div className="small fw-semibold">{group.label}</div>
                {/* DropZone only shows its label once you are ALREADY dragging,
                    so the feature was invisible to anyone who didn't know it was
                    there (Karen Sweatman 2026-08-12, who went looking for it on
                    the Upload button). Same resting hint as the Files tab. */}
                {group.id && uploadingFor !== group.id ? (
                  <span className="text-muted small d-none d-lg-inline">or drop files here</span>
                ) : null}
                {group.id ? (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="ms-auto"
                    disabled={uploadingFor === group.id}
                    onClick={() => pickFiles(group.id)}
                  >
                    {uploadingFor === group.id ? (
                      <><Spinner size="sm" animation="border" className="me-1" /> Uploading…</>
                    ) : (
                      <><FaUpload className="me-1" /> Upload</>
                    )}
                  </Button>
                ) : null}
              </div>

              {group.files.length ? (
                <div className="d-grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
                  {group.files.map((file) => (
                    <FileCard
                      key={file.id}
                      file={file}
                      onOpen={(f) => setLightboxId(f.id)}
                      onDelete={(f) => removeFile(group.id, f)}
                      onRename={renameFile}
                    />
                  ))}
                </div>
              ) : null}

              {!group.files.length ? (
                <div className="text-muted small">
                  No files yet. Drag quotes, sketches, notes or photos here, or use Upload.
                </div>
              ) : null}
            </DropZone>
          ))}
        </section>
      ) : null}

      {galleryImages.findIndex((f) => f.id === lightboxId) >= 0 ? (
        <Lightbox
          images={galleryImages}
          index={galleryImages.findIndex((f) => f.id === lightboxId)}
          onClose={() => setLightboxId(null)}
          onNav={(d) => {
            const next = galleryImages[galleryImages.findIndex((f) => f.id === lightboxId) + d];
            if (next) setLightboxId(next.id);
          }}
        />
      ) : null}
    </>
  );
}

export default ClaimInspectionTab;
